import { useState } from "react";
import { Box, Button, Chip, Paper, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import useStyles from "./styles";
import DatagridComponent from "../../../components/datagridComponent";
import ModalAtualizarScripts from "../modalAtualizarScripts";
import corNeutraChip from "../corNeutraChip";
import alvoId from "../alvoId";
import rotuloAlvo from "../rotuloAlvo";

// Acima deste limite de clientes, o painel comeca resumido e so expande sob demanda
const LIMITE_LISTA_DIRETA = 15;

export default function PainelSaudeColeta({
	saudeColeta,
	resumoPorCliente,
	scripts,
	onScriptAtualizado,
}) {
	const [expandido, setExpandido] = useState(false);
	const [clienteParaAtualizar, setClienteParaAtualizar] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	const lista = saudeColeta || [];
	const precisaResumo = lista.length > LIMITE_LISTA_DIRETA;
	const mostrarLista = !precisaResumo || expandido;

	// Contadores do resumo: sucesso, falha e nunca coletado (status null)
	const totalOk = lista.filter((cliente) => cliente?.status === "sucesso").length;
	const totalFalha = lista.filter((cliente) => cliente?.status === "falha").length;
	const totalNuncaColetado = lista.filter((cliente) => cliente?.status === null).length;

	// Par fundo/texto de cada status da coleta - sempre pares completos chip.background/colorText, com garantia de contraste
	// (status.activate/disabled tem alpha e o getContrastText do MUI ignora o alpha, dando texto ilegivel no tema claro).
	// "Nunca coletado" usa corNeutraChip - chip.background/colorText.default cru e quase transparente,
	// ilegivel contra fundo escuro e some contra a linha no fundo claro
	const corDoStatus = (status) => {
		if (status === "falha") {
			return {
				background: theme.palette.chip.background.critical,
				color: theme.palette.chip.colorText.critical,
			};
		}
		if (status === "sucesso") {
			return {
				background: theme.palette.chip.background.success,
				color: theme.palette.chip.colorText.success,
			};
		}
		return corNeutraChip(theme);
	};

	const labelDoStatus = (status) => {
		if (status === "sucesso") return "Coletado com sucesso";
		if (status === "falha") return "Falha na coleta";
		return "Nunca coletado";
	};

	// Linhas do grid com id estavel por alvo (cliente + proxy) e rotulo "Cliente · Proxy" - o DatagridComponent esconde a coluna id sozinho
	const rows = lista.map((cliente) => ({ ...cliente, id: alvoId(cliente), rotulo: rotuloAlvo(cliente) }));

	const columns = [
		{ field: "rotulo", headerName: "Cliente", flex: 1, minWidth: 160 },
		{
			field: "status",
			headerName: "Status",
			width: 180,
			renderCell: (params) => {
				const cor = corDoStatus(params.row?.status);
				return (
					<Chip
						size="small"
						label={labelDoStatus(params.row?.status)}
						sx={{
							...styles.chip,
							backgroundColor: cor.background,
							color: cor.color,
						}}
					/>
				);
			},
		},
		{
			field: "iniciadoEm",
			headerName: "Início",
			width: 170,
			renderCell: (params) =>
				params.row?.iniciadoEm
					? dayjs(params.row.iniciadoEm).format("DD/MM/YYYY HH:mm")
					: "—",
		},
		{
			field: "finalizadoEm",
			headerName: "Fim",
			width: 170,
			renderCell: (params) =>
				params.row?.finalizadoEm
					? dayjs(params.row.finalizadoEm).format("DD/MM/YYYY HH:mm")
					: "—",
		},
		{
			field: "motivo",
			headerName: "Motivo",
			flex: 1,
			minWidth: 160,
			renderCell: (params) =>
				params.row?.motivo ? params.row.motivo : "—",
		},
		// Colunas extras so quando o chamador passa o resumo por cliente (hoje so a aba Scripts) - a aba
		// Templates continua com as mesmas 5 colunas de sempre. Texto "X de Y" + botao de acao pra
		// atualizar os scripts daquele cliente (Fase 2)
		...(resumoPorCliente
			? [
					{
						field: "scriptsAtualizados",
						headerName: "Scripts atualizados",
						width: 170,
						renderCell: (params) => {
							const resumo = resumoPorCliente.get(alvoId(params.row));
							return resumo ? `${resumo.atualizados} de ${resumo.total}` : "—";
						},
					},
					{
						field: "acao",
						headerName: "",
						width: 160,
						sortable: false,
						renderCell: (params) => (
							<Button
								size="small"
								variant="outlined"
								sx={styles.botaoAcao}
								onClick={() =>
									setClienteParaAtualizar({
										clienteId: params.row?.clienteId,
										proxy: params.row?.proxy,
										nome: params.row?.nome,
									})
								}
							>
								Atualizar scripts
							</Button>
						),
					},
				]
			: []),
	];

	return (
		<>
			<Box sx={styles.container}>
				<Typography variant="subtitle1" sx={styles.titulo}>
					Saúde da coleta
				</Typography>
				{precisaResumo && (
					<Box sx={styles.resumoLinha}>
						<Typography variant="body2" sx={styles.resumoTexto}>
							{totalOk} ok, {totalFalha} falharam, {totalNuncaColetado} nunca coletados
						</Typography>
						<Button size="small" onClick={() => setExpandido((atual) => !atual)}>
							{expandido ? "Ocultar detalhes" : "Ver detalhes"}
						</Button>
					</Box>
				)}
				{mostrarLista && (
					// Paper garante o fundo/contraste corretos do grid nos dois modos, mesmo padrao ja usado em abaTemplates/Clientes/Staff/Home
					<Paper sx={styles.paper} elevation={theme.palette.mode === "dark" ? 0 : 3}>
						<DatagridComponent rows={rows} columns={columns} options={{ autoHeight: true }} />
					</Paper>
				)}
			</Box>
			{resumoPorCliente && (
				<ModalAtualizarScripts
					open={Boolean(clienteParaAtualizar)}
					onClose={() => setClienteParaAtualizar(null)}
					scripts={scripts}
					clienteId={clienteParaAtualizar?.clienteId}
					proxy={clienteParaAtualizar?.proxy}
					nomeCliente={clienteParaAtualizar?.nome}
					saudeColeta={saudeColeta}
					onConcluido={() => {
						onScriptAtualizado?.();
						setClienteParaAtualizar(null);
					}}
				/>
			)}
		</>
	);
}
